import { Stage, Layer, Rect, Line, Text, Group, Circle, Path } from "react-konva";
import { useDesign, payloadWithoutId } from "../../lib/store";
import { useCoarsePointer, useElementSize } from "../../lib/useViewportSize";
import { useStagePanZoom } from "../../lib/useStagePanZoom";
import { dragsImmediately, SLOP_PX, TOUCH_DRAG_DISTANCE } from "../../lib/touchPickup";
import { pressedTouch, startNodePickup, useTouchPickup } from "../../lib/useTouchPickup";
import { HoldHint } from "./HoldHint";
import { annotationScale, labelFits } from "../../lib/canvasLabels";
import { useMemo, useRef } from "react";
import type { KonvaEventObject } from "konva/lib/Node";
import { formatLength } from "../../lib/geom";
import { hostsMatch, resolveHostSegment } from "../../lib/interiorWalls";
import type { Opening, WallMount } from "../../lib/types";
import { Locate } from "lucide-react";
import { setCursor, setDragging } from "../../lib/presence";
import { useAuth } from "../../lib/auth";
import { useCollaborators } from "../../lib/useCollaborators";
import {
  WallCollaboratorCursors,
  WallCollaboratorSelections,
} from "./WallCollaboratorOverlay";
import { CollaboratorDragPreviewsWall } from "./CollaboratorDragPreviews";
import { elevationWallLook, segmentsPath } from "../../lib/finishes/plan2d";
import { interiorWallKey, outlineWallKey } from "../../lib/finishes/wallKeys";
import { ROOM_FINISHES_FLAG, useFlag } from "../../lib/flags";

interface Props {
  /** Whether dragging mounts is allowed. */
  interactive: boolean;
}

export function WallElevationCanvas({ interactive }: Props) {
  const [containerRef, size] = useElementSize<HTMLDivElement>();
  const room = useDesign((s) => s.design.room);
  const mounts = useDesign((s) => s.design.mounts);
  const openings = useDesign((s) => s.design.openings);
  const unit = useDesign((s) => s.design.unit);
  const wallIndex = useDesign((s) => s.activeWallIndex);
  const activeInterior = useDesign((s) => s.activeInteriorWall);
  const updateMount = useDesign((s) => s.updateMount);
  const updateOpening = useDesign((s) => s.updateOpening);
  const selectedMountId = useDesign((s) => s.selectedMountId);
  const selectMount = useDesign((s) => s.selectMount);
  const selectedOpeningId = useDesign((s) => s.selectedOpeningId);
  const selectOpening = useDesign((s) => s.selectOpening);
  const person = useDesign((s) => s.design.person);
  const showPerson = useDesign((s) => s.showPerson);
  const sharedRoomId = useDesign((s) => s.activeSharedRoomId);
  const setDraggingMount = useDesign((s) => s.setDraggingMount);
  const setDraggingOpening = useDesign((s) => s.setDraggingOpening);
  const addMount = useDesign((s) => s.addMount);
  const beginGesture = useDesign((s) => s.beginGesture);
  const endGesture = useDesign((s) => s.endGesture);
  const cancelGesture = useDesign((s) => s.cancelGesture);
  const auth = useAuth();
  const collaborators = useCollaborators(sharedRoomId, auth.user?.uid ?? null);
  const finishesOn = useFlag(ROOM_FINISHES_FLAG);

  const wall = useMemo(() => {
    const host = activeInterior
      ? {
          wallIndex: 0,
          interiorWallId: activeInterior.wallId,
          interiorSegment: activeInterior.segment,
        }
      : wallIndex == null
        ? null
        : { wallIndex };
    if (!host) return null;
    const seg = resolveHostSegment(room, host);
    if (!seg) return null;
    return { a: seg.a, b: seg.b, length: seg.len };
  }, [activeInterior, wallIndex, room]);

  function onActiveWall(item: { wallIndex: number; interiorWallId?: string; interiorSegment?: number }) {
    if (activeInterior) {
      return hostsMatch(item, {
        wallIndex: 0,
        interiorWallId: activeInterior.wallId,
        interiorSegment: activeInterior.segment,
      });
    }
    return !item.interiorWallId && item.wallIndex === wallIndex;
  }

  const PADDING = 60;
  const fit = useMemo(() => {
    if (!wall || size.width === 0 || size.height === 0) return null;
    // Short canvases (landscape phones) cap the padding so the wall keeps
    // at least half of each dimension.
    const padX = Math.min(PADDING, size.width * 0.25);
    const padY = Math.min(PADDING, size.height * 0.25);
    const availW = size.width - padX * 2;
    const availH = size.height - padY * 2;
    const wallW = wall.length;
    const wallH = room.ceilingHeight;
    const scale = Math.min(availW / wallW, availH / wallH);
    const usedW = wallW * scale;
    const usedH = wallH * scale;
    const x0 = (size.width - usedW) / 2;
    const y0 = (size.height - usedH) / 2;
    return { scale, x0, y0, w: usedW, h: usedH };
  }, [wall, size, room.ceilingHeight]);

  /** The chosen wall finish, echoed as color and hairlines (never a texture). */
  const finishLook = useMemo(() => {
    if (!finishesOn || !wall || !fit) return null;
    const key = activeInterior
      ? interiorWallKey(activeInterior.wallId, activeInterior.segment)
      : wallIndex != null
        ? outlineWallKey(wallIndex)
        : null;
    const look = elevationWallLook(room.finishes, key, wall.length, room.ceilingHeight, fit.scale);
    if (!look) return null;
    const floorY = fit.y0 + fit.h;
    return {
      ...look,
      path: segmentsPath(look.segments, (x) => fit.x0 + x * fit.scale, (y) => floorY - y * fit.scale),
    };
  }, [finishesOn, wall, fit, activeInterior, wallIndex, room.finishes, room.ceilingHeight]);

  const wallMounts = useMemo(
    () => mounts.filter((m) => onActiveWall(m)),
    // onActiveWall closes over the active host; those are the real deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mounts, wallIndex, activeInterior]
  );
  const wallOpenings = useMemo(
    () => openings.filter((o) => onActiveWall(o)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openings, wallIndex, activeInterior]
  );

  const coarse = useCoarsePointer();
  const pickup = useTouchPickup();
  const panZoom = useStagePanZoom({
    onInterrupt: () => {
      pickup.disarm();
      pickup.drop();
    },
  });
  const draggingRef = useRef<{ id: string } | null>(null);
  /** Scale for annotation text — see `canvasLabels.ts`. */
  const labelK = annotationScale(panZoom.scale);

  /** Whether an opening / mount name fits its `w` × `h` (stage px) box on screen. */
  function itemLabelFits(name: string, w: number, h: number) {
    const z = panZoom.scale;
    return labelFits(name, 11 * labelK * z, w * z, h * z, 6 * labelK * z);
  }

  /** Hold-to-pick-up for an opening or mount that isn't selected (touch). */
  function armPickup(e: KonvaEventObject<TouchEvent>, key: string, select: () => void) {
    const touch = pressedTouch(e);
    if (!touch) return;
    const node = e.currentTarget;
    const evt = e.evt;
    pickup.arm(key, touch, {
      onPickup: () => {
        select();
        startNodePickup(node, touch, evt);
      },
    });
  }
  /** Stashed on mousedown so Option/Alt+click+drag can clone even though
   *  Konva's dragstart fires after the pointer has already moved. */
  const cloneOnDragRef = useRef(false);

  function handleDragMove(e: KonvaEventObject<DragEvent>, m: WallMount) {
    if (!fit || !wall) return;
    const node = e.target;
    // Convert px center back to (alongWall, fromFloor)
    let alongWall = (node.x() - fit.x0) / fit.scale;
    let fromFloor = (fit.y0 + fit.h - node.y()) / fit.scale; // y inverted (floor at bottom)
    // Clamp so the item stays within the wall and ceiling
    const halfW = m.width / 2;
    const halfH = m.height / 2;
    alongWall = Math.max(halfW, Math.min(wall.length - halfW, alongWall));
    fromFloor = Math.max(halfH, Math.min(room.ceilingHeight - halfH, fromFloor));
    updateMount(m.id, { alongWall, fromFloor });
    // Snap visually
    node.x(fit.x0 + alongWall * fit.scale);
    node.y(fit.y0 + fit.h - fromFloor * fit.scale);
    if (sharedRoomId) {
      setDragging({
        kind: "mount",
        id: m.id,
        wallIndex: m.wallIndex,
        interiorWallId: m.interiorWallId ?? null,
        interiorSegment: m.interiorSegment ?? null,
        alongWall,
        fromFloor,
      });
    }
  }

  function handleOpeningDragMove(e: KonvaEventObject<DragEvent>, o: Opening) {
    if (!fit || !wall) return;
    const node = e.target;
    let alongWall = (node.x() - fit.x0) / fit.scale;
    const halfW = o.width / 2;
    const halfH = o.height / 2;
    alongWall = Math.max(halfW, Math.min(wall.length - halfW, alongWall));
    // Doors sit on the floor — vertical position is locked.
    let fromFloor = o.fromFloor;
    if (o.kind === "window") {
      fromFloor = (fit.y0 + fit.h - node.y()) / fit.scale;
      fromFloor = Math.max(halfH, Math.min(room.ceilingHeight - halfH, fromFloor));
    }
    updateOpening(o.id, { alongWall, fromFloor });
    node.x(fit.x0 + alongWall * fit.scale);
    node.y(fit.y0 + fit.h - fromFloor * fit.scale);
    if (sharedRoomId) {
      setDragging({
        kind: "opening",
        id: o.id,
        wallIndex: o.wallIndex,
        interiorWallId: o.interiorWallId ?? null,
        interiorSegment: o.interiorSegment ?? null,
        alongWall,
        fromFloor,
      });
    }
  }

  function broadcastWallCursor(e: KonvaEventObject<unknown>) {
    if (!sharedRoomId || !fit) return;
    const stage = e.target.getStage();
    if (!stage) return;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const transform = stage.getAbsoluteTransform().copy().invert();
    const local = transform.point(pointer);
    const alongWall = (local.x - fit.x0) / fit.scale;
    const fromFloor = (fit.y0 + fit.h - local.y) / fit.scale;
    setCursor({
      view: "wall",
      wallIndex: activeInterior ? null : (wallIndex ?? null),
      interiorWallId: activeInterior?.wallId ?? null,
      interiorSegment: activeInterior?.segment ?? null,
      x: alongWall,
      y: fromFloor,
    });
  }

  function handleStageMouseMove(e: KonvaEventObject<MouseEvent>) {
    broadcastWallCursor(e);
  }

  function handleStageMouseLeave() {
    if (sharedRoomId) setCursor(null);
  }

  if (!wall) {
    return (
      <div ref={containerRef} className="absolute inset-0 flex items-center justify-center text-ink-400">
        <div className="text-center">
          <div className="text-base font-medium text-ink-700">Choose a wall</div>
          <div className="text-sm">Tap any wall on the room plan to view it.</div>
        </div>
      </div>
    );
  }

  function clearStageSelection(e: KonvaEventObject<unknown>) {
    if (!interactive) return;
    if (e.target === e.target.getStage()) {
      selectMount(null);
      selectOpening(null);
    }
  }

  return (
    <div ref={containerRef} className="absolute inset-0 touch-none select-none">
      {size.width > 0 && size.height > 0 && fit && (
        <Stage
          ref={panZoom.stageRef}
          width={size.width}
          height={size.height}
          scaleX={panZoom.scale}
          scaleY={panZoom.scale}
          x={panZoom.x}
          y={panZoom.y}
          draggable
          dragDistance={coarse ? SLOP_PX : undefined}
          onDragEnd={panZoom.onDragEnd}
          onDragMove={(e) => broadcastWallCursor(e)}
          onWheel={(e) => {
            panZoom.onWheel(e);
            broadcastWallCursor(e);
          }}
          onTouchStart={panZoom.onTouchStart}
          onTouchMove={panZoom.onTouchMove}
          onTouchEnd={panZoom.onTouchEnd}
          onClick={clearStageSelection}
          onTap={clearStageSelection}
          onMouseMove={handleStageMouseMove}
          onMouseLeave={handleStageMouseLeave}
        >
          {/* Floor / ceiling reference */}
          <Layer listening={false}>
            {/* Floor band */}
            <Rect
              x={0}
              y={fit.y0 + fit.h}
              width={size.width}
              height={size.height - (fit.y0 + fit.h)}
              fill="#ece6dc"
            />
            {/* Sky / room interior */}
            <Rect
              x={0}
              y={0}
              width={size.width}
              height={fit.y0}
              fill="#f7f7f8"
            />
            {/* The wall rectangle */}
            <Rect
              x={fit.x0}
              y={fit.y0}
              width={fit.w}
              height={fit.h}
              fill={finishLook && finishLook.top >= room.ceilingHeight ? finishLook.fill : "#fafafb"}
              stroke="#0c0c10"
              strokeWidth={4}
            />
            {finishLook && finishLook.top < room.ceilingHeight && (
              <Rect
                x={fit.x0}
                y={fit.y0 + fit.h - finishLook.top * fit.scale}
                width={fit.w}
                height={finishLook.top * fit.scale}
                fill={finishLook.fill}
              />
            )}
            {finishLook && finishLook.path && (
              <Path data={finishLook.path} stroke={finishLook.line} strokeWidth={1} strokeScaleEnabled={false} />
            )}
            {/* Floor line indicator */}
            <Line
              points={[0, fit.y0 + fit.h, size.width, fit.y0 + fit.h]}
              stroke="#0c0c10"
              strokeWidth={2}
            />
            {/* Horizontal foot guides */}
            {Array.from({ length: Math.floor(room.ceilingHeight / 12) + 1 }).map(
              (_, i) => {
                const y = fit.y0 + fit.h - (i * 12) * fit.scale;
                if (y < fit.y0) return null;
                return (
                  <Group key={`hg-${i}`}>
                    <Line
                      points={[fit.x0 - 6, y, fit.x0, y]}
                      stroke="#0c0c10"
                      strokeWidth={1}
                    />
                    <Group x={fit.x0 - 10} y={y} scaleX={labelK} scaleY={labelK}>
                      <Text
                        x={-40}
                        y={-6}
                        width={40}
                        align="right"
                        text={`${i}'`}
                        fontSize={10}
                        fill="#6e6e7a"
                        fontFamily="Inter, sans-serif"
                      />
                    </Group>
                  </Group>
                );
              }
            )}
            {/* Wall width label */}
            <Group>
              <Line
                points={[fit.x0, fit.y0 - 14, fit.x0 + fit.w, fit.y0 - 14]}
                stroke="#6e6e7a"
                strokeWidth={1}
              />
              <Line
                points={[fit.x0, fit.y0 - 18, fit.x0, fit.y0 - 10]}
                stroke="#6e6e7a"
                strokeWidth={1}
              />
              <Line
                points={[fit.x0 + fit.w, fit.y0 - 18, fit.x0 + fit.w, fit.y0 - 10]}
                stroke="#6e6e7a"
                strokeWidth={1}
              />
              <Group x={fit.x0 + fit.w / 2} y={fit.y0 - 18} scaleX={labelK} scaleY={labelK}>
                <Text
                  x={-200}
                  y={-12}
                  width={400}
                  align="center"
                  text={formatLength(wall.length, unit)}
                  fontSize={11}
                  fontStyle="600"
                  fontFamily="Inter, sans-serif"
                  fill="#33333c"
                />
              </Group>
            </Group>
            {/* Ceiling label */}
            <Group x={fit.x0 + fit.w + 8} y={fit.y0 + 4} scaleX={labelK} scaleY={labelK}>
              <Text
                text={`${room.finishes?.setting === "outdoor" ? "Overhead" : "Ceiling"} ${formatLength(room.ceilingHeight, unit)}`}
                fontSize={10}
                fontFamily="Inter, sans-serif"
                fill="#6e6e7a"
              />
            </Group>
          </Layer>

          <Layer>
            {/* Person silhouette in front of wall, for scale */}
            {showPerson && (
              <PersonElevation
                heightPx={person.heightInches * fit.scale}
                shoulderPx={(17 + (person.weightLbs - 170) * 0.025) * fit.scale}
                x={fit.x0 + 70}
                yBaseline={fit.y0 + fit.h}
              />
            )}

            {/* Openings (doors + windows) render under mounts so a TV can sit
                in front of (overlap) a window in the elevation view. */}
            {wallOpenings.map((o) => {
              const cx = fit.x0 + o.alongWall * fit.scale;
              const cy = fit.y0 + fit.h - o.fromFloor * fit.scale;
              const w = o.width * fit.scale;
              const h = o.height * fit.scale;
              const selected = selectedOpeningId === o.id;
              const isCutout = o.kind === "door" && o.style === "cutout";
              const isGate = o.kind === "door" && o.style === "gate";
              const isDoor = o.kind === "door" && !isCutout;
              const immediate = dragsImmediately({ coarse, selected });
              return (
                <Group
                  key={o.id}
                  x={cx}
                  y={cy}
                  draggable={interactive && (immediate || pickup.liftedKey === o.id)}
                  dragDistance={coarse ? TOUCH_DRAG_DISTANCE : undefined}
                  onTouchStart={(e) => {
                    if (!interactive || immediate) return;
                    armPickup(e, o.id, () => selectOpening(o.id));
                  }}
                  onMouseDown={() => interactive && selectOpening(o.id)}
                  onTap={() => interactive && selectOpening(o.id)}
                  onDragStart={() => {
                    if (!interactive) return;
                    selectOpening(o.id);
                    beginGesture({ blockUndo: true });
                    if (sharedRoomId) {
                      setDraggingOpening(o.id);
                      setDragging({
                        kind: "opening",
                        id: o.id,
                        wallIndex: o.wallIndex,
                        alongWall: o.alongWall,
                        fromFloor: o.fromFloor,
                      });
                    }
                  }}
                  onDragMove={(e) => interactive && handleOpeningDragMove(e, o)}
                  onDragEnd={(e) => {
                    pickup.drop();
                    // No native event = stopped from code by a pinch.
                    if (e.evt) endGesture();
                    else cancelGesture();
                    if (!sharedRoomId) return;
                    setDraggingOpening(null);
                    setDragging(null);
                  }}
                >
                  <Rect
                    x={-w / 2}
                    y={-h / 2}
                    width={w}
                    height={h}
                    fill={isCutout ? "#ffffff" : isDoor ? "#f6efe2" : "#dfeaf6"}
                    stroke={selected ? "#2a70f5" : "#33333c"}
                    strokeWidth={selected ? 2.5 : 2}
                    cornerRadius={isDoor ? 2 : 0}
                  />
                  {/* Windows: a cross-frame mullion to feel like glazing */}
                  {o.kind === "window" && (
                    <>
                      <Line
                        points={[0, -h / 2, 0, h / 2]}
                        stroke="#7d97b3"
                        strokeWidth={1}
                      />
                      <Line
                        points={[-w / 2, 0, w / 2, 0]}
                        stroke="#7d97b3"
                        strokeWidth={1}
                      />
                    </>
                  )}
                  {/* Gate: vertical boards */}
                  {isGate &&
                    Array.from({ length: Math.max(0, Math.round(o.width / 5.5) - 1) }, (_, i) => {
                      const x = -w / 2 + ((i + 1) * w) / Math.round(o.width / 5.5);
                      return <Line key={i} points={[x, -h / 2, x, h / 2]} stroke="#b08a63" strokeWidth={1} />;
                    })}
                  {/* Door: handle dot on the right side */}
                  {isDoor && (
                    <Circle x={w / 2 - 6} y={0} radius={2} fill="#33333c" />
                  )}
                  {/* Label */}
                  {itemLabelFits(o.name, w, h) && (
                    <Text
                      x={-w / 2 + 6 * labelK}
                      y={-h / 2 + 4 * labelK}
                      scaleX={labelK}
                      scaleY={labelK}
                      width={(w - 12 * labelK) / labelK}
                      text={o.name}
                      fontSize={11}
                      fontStyle="600"
                      fontFamily="Inter, sans-serif"
                      fill="#33333c"
                      ellipsis
                      wrap="none"
                    />
                  )}
                  {selected && (
                    <Group y={h / 2 + 14 * labelK} scaleX={labelK} scaleY={labelK}>
                      <Rect
                        x={-60}
                        y={-9}
                        width={120}
                        height={18}
                        fill="#0c0c10"
                        cornerRadius={9}
                      />
                      <Text
                        x={-60}
                        y={-5}
                        width={120}
                        align="center"
                        text={
                          isCutout
                            ? `cutout · ${formatLength(o.width, unit)}`
                            : isGate
                              ? `gate · ${formatLength(o.width, unit)}`
                              : isDoor
                                ? `door · ${formatLength(o.width, unit)}`
                                : `sill @ ${formatLength(
                                    Math.max(0, o.fromFloor - o.height / 2),
                                    unit
                                  )}`
                        }
                        fontSize={11}
                        fontStyle="600"
                        fontFamily="Inter, sans-serif"
                        fill="#ffffff"
                      />
                    </Group>
                  )}
                </Group>
              );
            })}

            {/* Mounts */}
            {wallMounts.map((m) => {
              const cx = fit.x0 + m.alongWall * fit.scale;
              const cy = fit.y0 + fit.h - m.fromFloor * fit.scale;
              const w = m.width * fit.scale;
              const h = m.height * fit.scale;
              const selected = selectedMountId === m.id;
              const immediate = dragsImmediately({ coarse, selected });
              return (
                <Group
                  key={m.id}
                  x={cx}
                  y={cy}
                  draggable={interactive && (immediate || pickup.liftedKey === m.id)}
                  dragDistance={coarse ? TOUCH_DRAG_DISTANCE : undefined}
                  onTouchStart={(e) => {
                    if (!interactive || immediate) return;
                    armPickup(e, m.id, () => selectMount(m.id));
                  }}
                  onMouseDown={(e) => {
                    if (!interactive) return;
                    cloneOnDragRef.current = e.evt.altKey;
                    selectMount(m.id);
                  }}
                  onTap={() => interactive && selectMount(m.id)}
                  onDragStart={() => {
                    beginGesture({ blockUndo: true });
                    if (cloneOnDragRef.current) {
                      cloneOnDragRef.current = false;
                      addMount(payloadWithoutId(m), { select: false });
                    }
                    draggingRef.current = { id: m.id };
                    if (sharedRoomId) {
                      setDraggingMount(m.id);
                      setDragging({
                        kind: "mount",
                        id: m.id,
                        wallIndex: m.wallIndex,
                        alongWall: m.alongWall,
                        fromFloor: m.fromFloor,
                      });
                    }
                  }}
                  onDragMove={(e) => interactive && handleDragMove(e, m)}
                  onDragEnd={(e) => {
                    pickup.drop();
                    cloneOnDragRef.current = false;
                    draggingRef.current = null;
                    if (e.evt) endGesture();
                    else cancelGesture();
                    if (sharedRoomId) {
                      setDraggingMount(null);
                      setDragging(null);
                    }
                  }}
                >
                  <Rect
                    x={-w / 2}
                    y={-h / 2}
                    width={w}
                    height={h}
                    fill={m.color}
                    cornerRadius={Math.min(4, Math.min(w, h) * 0.08)}
                    stroke={selected ? "#2a70f5" : "rgba(0,0,0,0.2)"}
                    strokeWidth={selected ? 2 : 1}
                    shadowColor="#000"
                    shadowBlur={selected ? 16 : 8}
                    shadowOpacity={selected ? 0.25 : 0.12}
                    shadowOffsetY={4}
                  />
                  {/* TV screen highlight */}
                  {m.category === "tv" && (
                    <Rect
                      x={-w / 2 + 4}
                      y={-h / 2 + 4}
                      width={w - 8}
                      height={h - 8}
                      fill="#0e1626"
                      cornerRadius={2}
                    />
                  )}
                  {/* Center cross */}
                  {selected && (
                    <>
                      <Line
                        points={[-w / 2, 0, w / 2, 0]}
                        stroke="rgba(255,255,255,0.5)"
                        strokeWidth={1}
                        dash={[3, 3]}
                      />
                      <Line
                        points={[0, -h / 2, 0, h / 2]}
                        stroke="rgba(255,255,255,0.5)"
                        strokeWidth={1}
                        dash={[3, 3]}
                      />
                    </>
                  )}
                  {/* Label */}
                  {itemLabelFits(m.name, w, h) && (
                    <Text
                      x={-w / 2 + 6 * labelK}
                      y={-h / 2 + 6 * labelK}
                      scaleX={labelK}
                      scaleY={labelK}
                      width={(w - 12 * labelK) / labelK}
                      text={m.name}
                      fontSize={11}
                      fontStyle="600"
                      fontFamily="Inter, sans-serif"
                      fill="rgba(255,255,255,0.95)"
                      ellipsis
                      wrap="none"
                    />
                  )}
                  {/* Height label */}
                  {selected && (
                    <Group y={h / 2 + 14 * labelK} scaleX={labelK} scaleY={labelK}>
                      <Rect
                        x={-50}
                        y={-9}
                        width={100}
                        height={18}
                        fill="#0c0c10"
                        cornerRadius={9}
                      />
                      <Text
                        x={-50}
                        y={-5}
                        width={100}
                        align="center"
                        text={`center @ ${formatLength(m.fromFloor, unit)}`}
                        fontSize={11}
                        fontStyle="600"
                        fontFamily="Inter, sans-serif"
                        fill="#ffffff"
                      />
                    </Group>
                  )}
                </Group>
              );
            })}
          </Layer>

          {/* Collaborator selection halos + cursors. Both layers no-op
              when the user isn't in a shared room. */}
          {wall && (
            <>
              <Layer listening={false}>
                <WallCollaboratorSelections
                  collaborators={collaborators}
                  mounts={mounts}
                  openings={openings}
                  fit={{
                    scale: fit.scale,
                    x0: fit.x0,
                    y0: fit.y0,
                    w: wall.length,
                    h: room.ceilingHeight,
                  }}
                  activeWallIndex={wallIndex}
                  interiorWallId={activeInterior?.wallId ?? null}
                  interiorSegment={activeInterior?.segment ?? null}
                  stageScale={panZoom.scale}
                />
                <CollaboratorDragPreviewsWall
                  collaborators={collaborators}
                  mounts={mounts}
                  openings={openings}
                  fit={{
                    scale: fit.scale,
                    x0: fit.x0,
                    y0: fit.y0,
                    w: wall.length,
                    h: room.ceilingHeight,
                  }}
                  activeWallIndex={wallIndex}
                  interiorWallId={activeInterior?.wallId ?? null}
                  interiorSegment={activeInterior?.segment ?? null}
                />
              </Layer>
              <Layer listening={false}>
                <WallCollaboratorCursors
                  collaborators={collaborators}
                  fit={{
                    scale: fit.scale,
                    x0: fit.x0,
                    y0: fit.y0,
                    w: wall.length,
                    h: room.ceilingHeight,
                  }}
                  activeWallIndex={wallIndex}
                  interiorWallId={activeInterior?.wallId ?? null}
                  interiorSegment={activeInterior?.segment ?? null}
                  stageScale={panZoom.scale}
                />
              </Layer>
            </>
          )}
        </Stage>
      )}

      {panZoom.isAdjusted && (
        <button
          onClick={panZoom.reset}
          aria-label="Recenter"
          className="absolute right-3 bottom-3 z-10 inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white ring-1 ring-ink-200 text-xs font-medium text-ink-700 shadow-sm hover:bg-ink-50 active:bg-ink-100 transition-colors"
        >
          <Locate className="size-3.5" />
          Recenter
        </button>
      )}
      {interactive && <HoldHint show={pickup.holdHint} label="Hold an item to move it" />}
    </div>
  );
}

function PersonElevation({
  heightPx,
  shoulderPx,
  x,
  yBaseline,
}: {
  heightPx: number;
  shoulderPx: number;
  x: number;
  yBaseline: number;
}) {
  const headR = heightPx * 0.07;
  const headCy = yBaseline - heightPx + headR;
  const neckBottom = headCy + headR;
  const bodyW = shoulderPx;
  const torsoH = heightPx * 0.32;
  const torsoTop = neckBottom + headR * 0.4;
  const torsoBottom = torsoTop + torsoH;
  const hipsW = bodyW * 0.85;
  const legW = hipsW * 0.4;
  const legBottom = yBaseline;

  // Build a silhouette path
  const halfShoulder = bodyW / 2;
  const halfHip = hipsW / 2;
  const path = [
    `M ${-halfShoulder} ${torsoTop}`,
    `Q ${-halfShoulder * 1.05} ${torsoTop + torsoH * 0.4} ${-halfHip} ${torsoBottom}`,
    `L ${-halfHip + 2} ${torsoBottom + 2}`,
    `L ${-legW * 0.9} ${legBottom}`,
    `L ${-legW * 0.1} ${legBottom}`,
    `L ${-legW * 0.2} ${torsoBottom + 4}`,
    `L ${legW * 0.2} ${torsoBottom + 4}`,
    `L ${legW * 0.1} ${legBottom}`,
    `L ${legW * 0.9} ${legBottom}`,
    `L ${halfHip - 2} ${torsoBottom + 2}`,
    `L ${halfHip} ${torsoBottom}`,
    `Q ${halfShoulder * 1.05} ${torsoTop + torsoH * 0.4} ${halfShoulder} ${torsoTop}`,
    `Z`,
  ].join(" ");

  return (
    <Group x={x} y={0}>
      {/* Head */}
      <Circle x={0} y={headCy} radius={headR} fill="#2a70f5" opacity={0.9} />
      {/* Body silhouette */}
      <Path data={path} fill="#2a70f5" opacity={0.85} />
      {/* baseline dot */}
      <Circle x={0} y={yBaseline} radius={2} fill="#0c0c10" />
    </Group>
  );
}
